import React, { useMemo, useState } from "react";

import { COUNTRY_LIST, flagEmoji } from "../../../../constants/countries";

// Mirrors the AgentIntake "Nominee" phone control: a searchable dial-code
// combobox (flag + country + code) next to a masked mobile input. Self-contained
// so it has no dependency on the AgentIntake feature module.

const PHONE_MASKS = {
  "+971": "XX XXX XXXX",
  "+966": "XX XXX XXXX",
  "+974": "XXXX XXXX",
  "+965": "XXXX XXXX",
  "+973": "XXXX XXXX",
  "+968": "XXXX XXXX",
  "+91": "XXXXX XXXXX",
  "+44": "XXXX XXXXXX",
  "+1": "(XXX) XXX-XXXX",
};

export function formatPhone(raw, dial) {
  const mask = PHONE_MASKS[dial] || "";
  const digits = (raw || "").replace(/\D/g, "");
  if (!mask || !digits) return raw || "";
  let out = "";
  let i = 0;
  for (const m of mask) {
    if (i >= digits.length) break;
    out += m === "X" ? digits[i++] : m;
  }
  return out;
}

const labelStyle = { fontSize: "11.5px", fontWeight: 600, color: "#5A6B73" };
const controlBase = {
  width: "100%",
  border: "1px solid #DCE3E8",
  borderRadius: "8px",
  padding: "10px 12px",
  fontSize: "13.5px",
  fontFamily: "inherit",
  color: "#002940",
  outline: "none",
  boxSizing: "border-box",
  background: "#fff",
};

const DialPhoneField = ({ dial = "+971", onDialChange, phone, onPhoneChange, error }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const selected = useMemo(() => COUNTRY_LIST.find((c) => c.dial === dial), [dial]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return COUNTRY_LIST;
    return COUNTRY_LIST.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.dial.includes(q) ||
        c.code.toLowerCase().includes(q)
    );
  }, [search]);

  const example = (PHONE_MASKS[dial] || "").replace(/X/g, "0");

  const pickDial = (c) => {
    onDialChange(c.dial);
    setOpen(false);
    setSearch("");
  };

  return (
    <div style={{ display: "grid", gridTemplateColumns: "150px 1fr", gap: "10px" }}>
      {/* Country code */}
      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
        <label style={labelStyle}>Country code</label>
        <div style={{ position: "relative" }}>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            style={{
              ...controlBase,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "8px",
              cursor: "pointer",
              borderColor: error ? "#E11D48" : "#DCE3E8",
            }}
          >
            <span style={{ display: "flex", alignItems: "center", gap: "8px", overflow: "hidden" }}>
              <span style={{ fontSize: "16px" }}>{selected ? flagEmoji(selected.code) : "🏳️"}</span>
              <span style={{ fontWeight: 600 }}>{dial}</span>
            </span>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#9AA8AF" strokeWidth="2">
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>

          {open && (
            <>
              <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 35 }} />
              <div
                style={{
                  position: "absolute",
                  top: "46px",
                  left: 0,
                  width: "300px",
                  maxWidth: "80vw",
                  background: "#fff",
                  border: "1px solid #E4EAEE",
                  borderRadius: "11px",
                  boxShadow: "0 14px 36px rgba(0,30,48,.16)",
                  padding: "8px",
                  zIndex: 40,
                }}
              >
                <input
                  autoFocus
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search code / country…"
                  style={{ ...controlBase, marginBottom: "6px" }}
                />
                <div style={{ maxHeight: "220px", overflowY: "auto" }}>
                  {filtered.map((c) => (
                    <button
                      type="button"
                      key={c.code}
                      onClick={() => pickDial(c)}
                      style={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        padding: "8px 10px",
                        border: "none",
                        background: c.dial === dial ? "#FFF1ED" : "transparent",
                        borderRadius: "8px",
                        cursor: "pointer",
                        fontSize: "13px",
                        fontFamily: "inherit",
                        textAlign: "left",
                      }}
                      onMouseEnter={(e) => {
                        if (c.dial !== dial) e.currentTarget.style.background = "#F3F6F8";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = c.dial === dial ? "#FFF1ED" : "transparent";
                      }}
                    >
                      <span style={{ fontSize: "16px" }}>{flagEmoji(c.code)}</span>
                      <span style={{ flex: 1, color: "#002940", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {c.name}
                      </span>
                      <span style={{ color: "#7C8B92", fontWeight: 600 }}>{c.dial}</span>
                    </button>
                  ))}
                  {filtered.length === 0 && (
                    <div style={{ padding: "10px", fontSize: "13px", color: "#9AA8AF" }}>No matches</div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Mobile */}
      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
        <label style={labelStyle}>Mobile</label>
        <input
          value={formatPhone(phone, dial)}
          onChange={(e) => onPhoneChange(formatPhone(e.target.value, dial))}
          placeholder={example || "Phone number"}
          inputMode="tel"
          style={{ ...controlBase, borderColor: error ? "#E11D48" : "#DCE3E8" }}
          onFocus={(e) => (e.currentTarget.style.borderColor = "#FB512D")}
          onBlur={(e) => (e.currentTarget.style.borderColor = error ? "#E11D48" : "#DCE3E8")}
        />
      </div>
    </div>
  );
};

export default DialPhoneField;
